import { GOOGLE_FONTS } from '@agency-smm/shared';

// Track which fonts have been loaded to avoid duplicate loading
const loadedFonts = new Set<string>();
let allFontsLoaded = false;

// Google Fonts CSS2 API base
const GOOGLE_FONTS_API = 'https://fonts.googleapis.com/css2';

/**
 * Load a single Google Font via FontFace API.
 * Fetches the CSS from Google Fonts, extracts the font file URL,
 * and registers it with the document.
 */
async function loadSingleFont(fontName: string): Promise<void> {
  if (loadedFonts.has(fontName)) return;

  try {
    // Fetch CSS from Google Fonts with weights 400,600,700,800,900
    const url = `${GOOGLE_FONTS_API}?family=${encodeURIComponent(fontName)}:wght@400;600;700;800;900&display=swap`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch font CSS for ${fontName}`);

    const css = await response.text();

    // Extract all @font-face src urls and weights from the CSS
    const fontFaceRegex = /font-weight:\s*(\d+);[^}]*?src:\s*url\(([^)]+)\)\s*format\(['"]([^'"]+)['"]\)/g;
    let match: RegExpExecArray | null;

    const loadPromises: Promise<void>[] = [];

    while ((match = fontFaceRegex.exec(css)) !== null) {
      const weight = match[1];
      const fontUrl = match[2];
      const format = match[3];

      // Only load woff2 for best compression
      if (format === 'woff2' || format.includes('woff2')) {
        const face = new FontFace(fontName, `url(${fontUrl})`, {
          weight,
          style: 'normal',
          display: 'swap',
        });

        loadPromises.push(
          face.load().then((loadedFace) => {
            document.fonts.add(loadedFace);
          })
        );
      }
    }

    // If regex didn't match (CSS format varies), try a simpler approach
    if (loadPromises.length === 0) {
      const simpleUrlMatch = css.match(/url\(([^)]+\.woff2[^)]*)\)/);
      if (simpleUrlMatch) {
        const face = new FontFace(fontName, `url(${simpleUrlMatch[1]})`, {
          weight: '400 900',
          style: 'normal',
          display: 'swap',
        });
        await face.load();
        document.fonts.add(face);
      }
    } else {
      await Promise.all(loadPromises);
    }

    loadedFonts.add(fontName);
  } catch (err) {
    console.warn(`Failed to load Google Font "${fontName}":`, err);
    // Don't throw - canvas will fall back to system font
  }
}

/**
 * Preload all Google Fonts used in templates.
 * Call this before canvas rendering to ensure fonts are available.
 * Safe to call multiple times - only loads each font once.
 */
export async function preloadAllFonts(): Promise<void> {
  if (allFontsLoaded) return;

  await Promise.all(GOOGLE_FONTS.map((font) => loadSingleFont(font)));
  allFontsLoaded = true;
}

// System fonts that don't need loading
const SYSTEM_FONTS = ['Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Verdana', 'Impact', 'Courier New', 'serif', 'sans-serif', 'monospace'];

/**
 * Ensure a specific font is loaded before rendering.
 * Tries to load any non-system font via Google Fonts API.
 */
export async function ensureFontLoaded(fontName: string): Promise<void> {
  if (!fontName || SYSTEM_FONTS.includes(fontName)) return;
  await loadSingleFont(fontName);
}

/**
 * Ensure all fonts used in a template are loaded.
 */
export async function ensureTemplateFontsLoaded(elements: Array<{ elementType: string; fontFamily?: string }>): Promise<void> {
  const fontNames = new Set<string>();
  for (const el of elements) {
    if (el.elementType === 'text' && el.fontFamily) {
      fontNames.add(el.fontFamily);
    }
  }

  await Promise.all(
    Array.from(fontNames).map((font) => ensureFontLoaded(font))
  );
}
